import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import adminQueueService from '../../../../../services/adminQueueService';

const EMPTY_FORM = {
	name: '',
	surname: '',
	patronymic: '',
	email: '',
	password: '',
	phoneNumber: '',
	serviceTypeIds: [],
	windowNumber: ''
};

function ManagerModal({ open, onClose, onSuccess, editingItem, serviceTypes = [], windows = [] }) {
	const { t } = useTranslation();
	const isEdit = !!editingItem;

	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [apiError, setApiError] = useState('');

	useEffect(() => {
		if (open) {
			setApiError('');
			setErrors({});
			if (editingItem) {
				setForm({
					name: editingItem.name || '',
					surname: editingItem.surname || '',
					patronymic: editingItem.patronymic || '',
					email: editingItem.email || '',
					password: '',
					phoneNumber: editingItem.phoneNumber || '',
					serviceTypeIds: (editingItem.serviceTypes || []).map((st) => st.id),
					windowNumber: editingItem.windowNumber || ''
				});
			} else {
				setForm(EMPTY_FORM);
			}
		}
	}, [open, editingItem]);

	const validate = () => {
		const e = {};
		if (!form.name.trim()) e.name = t('common.required');
		if (!form.surname.trim()) e.surname = t('common.required');
		if (!form.email.trim()) e.email = t('common.required');
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
			e.email = t('admin.managers.validEmail');
		if (!form.phoneNumber.trim()) e.phoneNumber = t('common.required');
		else if (!/^\d+$/.test(form.phoneNumber))
			e.phoneNumber = t('admin.managers.validDigitsOnly');
		if (!isEdit && !form.password) e.password = t('common.required');
		if (
			form.password &&
			!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/.test(form.password)
		)
			e.password = t('admin.managers.validPassword');
		if (!form.serviceTypeIds || form.serviceTypeIds.length === 0)
			e.serviceTypeIds = t('admin.managers.fieldServiceTypeError');
		if (!form.windowNumber.trim()) e.windowNumber = t('common.required');
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const handleChange = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: e.target.value }));
		if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
		if (apiError) setApiError('');
	};

	const handleSubmit = async () => {
		if (!validate()) return;
		setLoading(true);
		setApiError('');
		try {
			const userPayload = {
				name: form.name,
				surname: form.surname,
				patronymic: form.patronymic || null,
				email: form.email,
				password: form.password || null,
				phoneNumber: form.phoneNumber
			};
			if (isEdit) {
				await adminQueueService.updateManager(editingItem.id, {
					user: userPayload,
					serviceTypeIds: form.serviceTypeIds,
					windowNumber: form.windowNumber
				});
				toast.success(t('admin.managers.updatedSuccess'));
			} else {
				await adminQueueService.createManager({
					user: { ...userPayload, password: form.password },
					serviceTypeIds: form.serviceTypeIds,
					windowNumber: form.windowNumber
				});
				toast.success(t('admin.managers.createdSuccess'));
			}
			onSuccess();
		} catch (e) {
			const msg =
				e?.response?.data?.message ||
				e?.response?.data?.error ||
				t('common.errorSave');
			setApiError(msg);
		} finally {
			setLoading(false);
		}
	};

	// Показываем только свободные окна; при редактировании оставляем текущее окно менеджера
	const availableWindows = windows.filter(
		(w) => !w.occupied || String(w.number) === String(form.windowNumber)
	);

	return (
		<Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
			<DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
				{isEdit ? t('admin.managers.modalEditTitle') : t('admin.managers.modalAddTitle')}
			</DialogTitle>

			<DialogContent>
				{apiError && (
					<Alert severity='error' sx={{ mb: 2 }}>
						{apiError}
					</Alert>
				)}

				<Stack spacing={2.5} sx={{ pt: 1 }}>
					<Typography
						variant='caption'
						color='text.secondary'
						sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
					>
						{t('admin.managers.sectionPersonal')}
					</Typography>

					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
						<TextField
							label={t('admin.managers.fieldSurname')}
							fullWidth
							size='small'
							value={form.surname}
							onChange={handleChange('surname')}
							error={!!errors.surname}
							helperText={errors.surname}
						/>
						<TextField
							label={t('admin.managers.fieldName')}
							fullWidth
							size='small'
							value={form.name}
							onChange={handleChange('name')}
							error={!!errors.name}
							helperText={errors.name}
						/>
					</Stack>

					<TextField
						label={t('admin.managers.fieldPatronymic')}
						fullWidth
						size='small'
						value={form.patronymic}
						onChange={handleChange('patronymic')}
					/>

					<Divider />

					<Typography
						variant='caption'
						color='text.secondary'
						sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
					>
						{t('admin.managers.sectionAccount')}
					</Typography>

					<TextField
						label={t('admin.managers.fieldEmail')}
						type='email'
						fullWidth
						size='small'
						value={form.email}
						onChange={handleChange('email')}
						error={!!errors.email}
						helperText={errors.email}
					/>

					<TextField
						label={isEdit ? t('admin.managers.fieldPasswordEdit') : t('admin.managers.fieldPassword')}
						type='password'
						fullWidth
						size='small'
						value={form.password}
						onChange={handleChange('password')}
						error={!!errors.password}
						helperText={errors.password || t('admin.managers.fieldPasswordHint')}
					/>

					<TextField
						label={t('admin.managers.fieldPhone')}
						fullWidth
						size='small'
						value={form.phoneNumber}
						onChange={handleChange('phoneNumber')}
						error={!!errors.phoneNumber}
						helperText={errors.phoneNumber || t('admin.managers.fieldPhoneHint')}
						inputProps={{ inputMode: 'numeric' }}
					/>

					<Divider />

					<Typography
						variant='caption'
						color='text.secondary'
						sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
					>
						{t('admin.managers.sectionQueue')}
					</Typography>

					<FormControl fullWidth size='small' error={!!errors.serviceTypeIds}>
						<InputLabel>{t('admin.managers.fieldServiceTypes')}</InputLabel>
						<Select
							multiple
							value={form.serviceTypeIds}
							onChange={handleChange('serviceTypeIds')}
							input={<OutlinedInput label={t('admin.managers.fieldServiceTypes')} />}
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((id) => {
										const st = serviceTypes.find((s) => s.id === id);
										return (
											<Chip
												key={id}
												label={st?.nameRu || id}
												size='small'
												sx={{ backgroundColor: st?.color || '#EDE9FE', fontWeight: 600 }}
											/>
										);
									})}
								</Box>
							)}
						>
							{serviceTypes.map((st) => (
								<MenuItem key={st.id} value={st.id}>
									<Box
										sx={{
											width: 14,
											height: 14,
											borderRadius: 0.5,
											backgroundColor: st.color || '#EDE9FE',
											border: '1px solid rgba(0,0,0,0.12)',
											mr: 1,
											flexShrink: 0
										}}
									/>
									{st.nameRu}
								</MenuItem>
							))}
						</Select>
						{errors.serviceTypeIds && (
							<FormHelperText>{errors.serviceTypeIds}</FormHelperText>
						)}
					</FormControl>

					<FormControl fullWidth size='small' error={!!errors.windowNumber}>
						<InputLabel>{t('admin.managers.fieldWindow')}</InputLabel>
						<Select
							value={form.windowNumber}
							onChange={handleChange('windowNumber')}
							input={<OutlinedInput label={t('admin.managers.fieldWindow')} />}
						>
							{availableWindows.length === 0 && (
								<MenuItem value='' disabled>
									{t('admin.managers.fieldWindowEmpty')}
								</MenuItem>
							)}
							{availableWindows.map((w) => (
								<MenuItem key={w.id} value={String(w.number)}>
									{w.number}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>
							{errors.windowNumber || t('admin.managers.fieldWindowSelectHint')}
						</FormHelperText>
					</FormControl>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
				<Button
					onClick={onClose}
					disabled={loading}
					sx={{ textTransform: 'none', borderRadius: 2 }}
				>
					{t('common.cancel')}
				</Button>
				<Button
					variant='contained'
					onClick={handleSubmit}
					disabled={loading}
					sx={{ textTransform: 'none', borderRadius: 2, minWidth: 130 }}
				>
					{loading ? (
						<CircularProgress size={18} sx={{ color: 'inherit' }} />
					) : isEdit ? (
						t('common.save')
					) : (
						t('common.add')
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ManagerModal;
